import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Upload, Button, Space, Typography, message, List, Tag } from 'antd';
import { UploadOutlined, MailOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface EmailModalProps {
  visible: boolean;
  onClose: () => void;
  lead: Lead | null;
}

interface EmailFormData {
  destinatario: string;
  recipient: string;
  subject: string;
  body: string;
}

const EmailModal: React.FC<EmailModalProps> = ({ visible, onClose, lead }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [emailFiles, setEmailFiles] = useState<File[]>([]);

  const destinatarioOptions = [
    { value: 'vodafone', label: 'Vodafone' },
    { value: 'nos', label: 'NOS' }
  ];

  const recipientOptions = [
    { value: 'documentacao', label: 'Documentação' },
    { value: 'suporte', label: 'Pedido de Suporte' },
    { value: 'informacao', label: 'Informação' }
  ];

  // Pre-fill subject based on recipient selection and lead data
  const handleRecipientChange = (recipient: string) => {
    if (!lead) return;
    
    let subject = '';
    switch (recipient) {
      case 'documentacao':
        subject = `Documentação - ${lead.name}`;
        if (lead.dif) {
          subject += ` - ${lead.dif}`;
        }
        break;
      case 'suporte':
        subject = `Pedido de Suporte - ${lead.name}`;
        break;
      case 'informacao':
        subject = `Informação - ${lead.name}`;
        break;
      default:
        subject = '';
    }
    
    form.setFieldsValue({ subject });
  };

  const handleFileChange = (info: any) => {
    const newFiles = info.fileList.map((f: any) => f.originFileObj).filter(Boolean);
    setEmailFiles(newFiles);
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setEmailFiles(files => files.filter(file => file !== fileToRemove));
    message.success('Ficheiro removido com sucesso');
  };

  const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  };

  const getTotalAttachmentSize = () => {
    let total = 0;
    emailFiles.forEach(f => { if (f && f.size) total += f.size; });
    return total;
  };

  const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20 MB

  const handleSendEmail = async () => {
    try {
      const values = await form.validateFields();
      
      const totalSize = getTotalAttachmentSize();
      if (totalSize > MAX_ATTACHMENT_SIZE) {
        message.error('O tamanho total dos anexos excede o limite permitido (20 MB). Remova alguns ficheiros e tente novamente.');
        return;
      }

      setLoading(true);
      
      const res = await apiService.sendEmailWithAttachments({
        subject: values.subject,
        body: values.body,
        files: emailFiles,
        destinatario: values.destinatario
      });

      if (res.success) {
        message.success('E-mail enviado com sucesso!');
        handleClose();
      } else {
        message.error(res.message || 'Falha ao enviar o e-mail.');
      }
    } catch (error) {
      message.error('Erro ao enviar o e-mail.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setEmailFiles([]);
    onClose();
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setEmailFiles([]);
    }
  }, [visible, form]);

  return (
    <Modal
      title={
        <Space>
          <MailOutlined />
          Enviar E-mail
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={loading}
          onClick={handleSendEmail}
        >
          Enviar E-mail
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="recipient"
          label="Propósito"
          rules={[{ required: true, message: 'Por favor selecione o propósito' }]}
        >
          <Select
            placeholder="Selecione o propósito"
            onChange={handleRecipientChange}
          >
            {recipientOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="destinatario"
          label="Destinatário"
          rules={[{ required: true, message: 'Por favor selecione o destinatário' }]}
        >
          <Select placeholder="Selecione o destinatário">
            {destinatarioOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="subject"
          label="Assunto"
          rules={[{ required: true, message: 'Por favor insira o assunto' }]}
        >
          <Input placeholder="Assunto do e-mail" />
        </Form.Item>

        <Form.Item
          name="body"
          label="Mensagem"
          rules={[{ required: true, message: 'Por favor insira a mensagem' }]}
        >
          <TextArea
            rows={6}
            placeholder="Escreva a sua mensagem aqui..."
          />
        </Form.Item>

        <Form.Item label="Anexar ficheiros">
          <Upload
            multiple
            beforeUpload={() => false}
            onChange={handleFileChange}
            fileList={emailFiles.map((f, index) => ({ 
              uid: `${f.name}-${index}`, 
              name: f.name, 
              status: 'done', 
              size: f.size 
            }))}
            onRemove={(file) => {
              const fileIndex = emailFiles.findIndex(f => f.name === file.name);
              if (fileIndex !== -1) {
                handleRemoveFile(emailFiles[fileIndex]);
              }
              return true;
            }}
            showUploadList={{
              showPreviewIcon: false,
              showDownloadIcon: false,
              showRemoveIcon: true,
            }}
          >
            <Button icon={<UploadOutlined />}>Selecionar ficheiros</Button>
          </Upload>
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Pode anexar múltiplos ficheiros do seu PC. Tamanho máximo total: 20 MB.
          </Text>
          
          {emailFiles.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Ficheiros anexados ({emailFiles.length}):</Text>
              <List
                size="small"
                dataSource={emailFiles}
                renderItem={(file, index) => (
                  <List.Item
                    key={`${file.name}-${index}`}
                    actions={[
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(file)}
                        title="Remover ficheiro"
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined />}
                      title={
                        <Space>
                          <Text>{file.name}</Text>
                          <Tag color="blue">{formatBytes(file.size)}</Tag>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
                style={{ 
                  maxHeight: 200, 
                  overflowY: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: 8
                }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tamanho total: {formatBytes(getTotalAttachmentSize())}
              </Text>
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EmailModal;
